import React, { useState, useEffect } from "react";
import { TournamentBracket } from "./TournamentBracket";
import NarrativeSection from "./MatchScreen/NarrativeSection";
import MatchScreen from "./MatchScreen/MatchScreen";
import { ChampionView } from "./ChampionView";
import { VersusScreen } from "./VersusScreen";
import { useNarrative } from "../hooks/useNarrative";
import { useTournament } from "../contexts/TournamentContext";
import { DEV_CONFIG } from "../config/dev";
import { soundManager, SOUNDS } from "../utils/soundManager";

interface GameContainerProps {
  onReturnHome?: () => void;
}

export const GameContainer: React.FC<GameContainerProps> = ({
  onReturnHome,
}) => {
  const narrative = useNarrative();
  const { tournament } = useTournament();
  const [hasClickedIntro, setHasClickedIntro] = useState(false);
  const [showVersusScreen, setShowVersusScreen] = useState(false);

  // Get finals and semi-finals matches from the shared tournament
  const finalsMatch = tournament.rounds[0]?.matches[0];
  const semiFinalsMatches = tournament.rounds[1]?.matches || [];

  // Derive view from current narrative key
  const currentView = (() => {
    if (!narrative.currentKey) return "bracket";

    // Map narrative keys to views
    if (narrative.currentKey.startsWith("bracket")) {
      return "bracket";
    }

    if (narrative.currentKey === "bracket.versus") {
      return "versus";
    }

    if (narrative.currentKey.startsWith("finals")) {
      return "battle";
    }

    if (narrative.currentKey.startsWith("champion")) {
      return "champion";
    }

    if (narrative.currentKey.startsWith("reveal")) {
      return "champion"; // Stay on champion screen instead of CTA
    }

    return "bracket";
  })();

  // Initialize the game
  useEffect(() => {
    // Initialize sound manager
    soundManager.init();

    if (DEV_CONFIG.narrativeKey && process.env.NODE_ENV === "development") {
      narrative.jumpToKey(DEV_CONFIG.narrativeKey);
    }

    // Set initial narrative variables from actual tournament data
    if (finalsMatch && finalsMatch.tokenA && finalsMatch.tokenB) {
      const tokenA = finalsMatch.tokenA;
      const tokenB = finalsMatch.tokenB;
      // For the demo, tokenA (Shogunate) wins
      const winner = tokenA;
      const loser = tokenB;

      narrative.setVariables({
        token1: tokenA.symbol,
        token2: tokenB.symbol,
        token3: semiFinalsMatches[0]?.tokenA?.symbol || "TOKEN3",
        token4: semiFinalsMatches[0]?.tokenB?.symbol || "TOKEN4",
        liquidity1: Math.floor(tokenA.liquidity).toLocaleString(),
        liquidity2: Math.floor(tokenB.liquidity).toLocaleString(),
        totalLiquidity: Math.floor(tournament.totalLiquidity).toLocaleString(),
        winner: winner.symbol,
        loser: loser.symbol,
        amount: Math.floor(loser.liquidity).toLocaleString(),
        remainingTokens: 4,
      });
    }
  }, [finalsMatch, semiFinalsMatches, tournament.totalLiquidity]);

  // Track if we should trigger bracket scroll animation
  const [shouldAnimateBracket, setShouldAnimateBracket] = useState(false);
  const [scrollSpeed, setScrollSpeed] = useState<"slow" | "fast">("slow");

  // Callback to track when user clicks during intro
  const handleNarrativeClick = () => {
    if (narrative.currentKey === "intro.line1" && !hasClickedIntro) {
      setHasClickedIntro(true);
      setShouldAnimateBracket(true);
      setScrollSpeed("slow");
    }
  };

  // Handle narrative-specific triggers
  useEffect(() => {
    if (!narrative.currentKey) return;

    // Trigger specific actions based on narrative key
    switch (narrative.currentKey) {
      case "intro.line1":
        // Start background music when intro begins
        soundManager.playBackgroundMusic(SOUNDS.BGM, 0.3).catch((err) => {
          console.error("Failed to play BGM:", err);
        });
        console.log("Starting background music");
        break;
      case "bracket.liquidity":
        // Speed up the scroll animation if already running, or start it fast
        if (!shouldAnimateBracket) {
          setShouldAnimateBracket(true);
        }
        setScrollSpeed("fast");
        console.log("Bracket scroll speed increased");
        break;
      case "finals.start":
        // Initialize battle state
        console.log("Battle starting...");
        break;
      case "finals.betting":
        // Enable user interaction
        console.log("Betting phase active");
        break;
      case "finals.rally":
        // Trigger momentum shift animation
        console.log("Rally animation triggered");
        break;
      case "finals.victory":
        // Play victory animation + liquidity transfer
        console.log("Victory sequence initiated");
        // Stop battle music when victory starts
        soundManager.stopBackgroundMusic();
        break;
      case "bracket.versus":
        // Show versus screen (VersusScreen component will handle music transition)
        setShowVersusScreen(true);
        console.log("Showing versus screen");
        break;
      case "bracket.ready":
        // Ready to transition to battle
        console.log("Ready for battle transition");
        break;
      default:
        break;
    }
  }, [narrative.currentKey]);

  return (
    <div className="relative h-screen w-screen bg-black overflow-hidden">
      {currentView === "bracket" && (
        <div className="relative h-full w-full flex flex-col">
          <div className="flex-1 overflow-hidden">
            <TournamentBracket
              shouldAnimateScroll={shouldAnimateBracket}
              scrollSpeed={scrollSpeed}
            />
          </div>
          <div className="fixed bottom-0 left-0 right-0 z-40">
            <NarrativeSection onNarrativeClick={handleNarrativeClick} />
          </div>
        </div>
      )}

      {currentView === "battle" && <MatchScreen scriptedWinner="A" />}

      {currentView === "champion" && (
        <ChampionView onReturnHome={onReturnHome} />
      )}

      {/* Versus Screen Overlay */}
      <VersusScreen
        isVisible={showVersusScreen}
        onComplete={() => {
          setShowVersusScreen(false);
          // Progress to finals after versus screen
          narrative.advanceNarrative();
        }}
      />
    </div>
  );
};
